import React, { useEffect, useState } from 'react';
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography,
} from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import { EmployeeAutocomplete } from '../common/EmployeeAutocomplete';
import { Project, ProjectRequest, ProjectStatus } from '../../interfaces/project';
import { ProjectService } from '../../services/project.service';
import { CustomerService } from '../../services/customer.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { resetProjectOptionsCache } from './ProjectSelect';
import DepartmentPicker, { useDepartmentOptions } from '../costcentre/DepartmentPicker';

interface NamedOption { id: string; name: string }
interface UserOption { id: string; username: string }

/** P1-A: project status chip colours and labels shared by the list and the detail page. */
export const statusColor: Record<ProjectStatus, 'default' | 'success' | 'warning' | 'info'> = {
    PLANNED: 'info', ACTIVE: 'success', ON_HOLD: 'warning', CLOSED: 'default',
};
export const statusLabel: Record<ProjectStatus, string> = {
    PLANNED: 'Planned', ACTIVE: 'Active', ON_HOLD: 'On hold', CLOSED: 'Closed',
};

/** The A-1 transition table, mirrored from ProjectStatus.canMoveTo. */
export const nextStatuses = (s: ProjectStatus): ProjectStatus[] => ({
    PLANNED: ['ACTIVE', 'ON_HOLD', 'CLOSED'],
    ACTIVE: ['ON_HOLD', 'CLOSED'],
    ON_HOLD: ['ACTIVE', 'CLOSED'],
    CLOSED: ['ACTIVE'],
} as Record<ProjectStatus, ProjectStatus[]>)[s];

export const actionLabel = (from: ProjectStatus, to: ProjectStatus): string =>
    to === 'CLOSED' ? 'Close' : from === 'CLOSED' ? 'Reopen' : to === 'ON_HOLD' ? 'Put on hold' : 'Activate';

const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));

interface FormProps {
    open: boolean;
    project?: Project | null;
    onClose: () => void;
    onSaved: (p: Project) => void;
}

/** New / edit project. Customer and manager are optional; budgets are plain numbers. */
export const ProjectFormDialog: React.FC<FormProps> = ({ open, project, onClose, onSaved }) => {
    const toast = useToast();
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [manager, setManager] = useState<UserOption | null>(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [budgetCost, setBudgetCost] = useState('');
    const [budgetRevenue, setBudgetRevenue] = useState('');
    const [allowStaff, setAllowStaff] = useState(true);
    const [customers, setCustomers] = useState<NamedOption[]>([]);
    const [departmentId, setDepartmentId] = useState('');
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCode(project?.code ?? '');
        setName(project?.name ?? '');
        setDescription(project?.description ?? '');
        setCustomerId(project?.customerId ?? '');
        setDepartmentId(project?.departmentId ?? '');
        setManager(project?.managerUserId ? { id: project.managerUserId, username: project.managerName ?? '' } : null);
        setStartDate(project?.startDate ?? '');
        setEndDate(project?.endDate ?? '');
        setBudgetCost(project?.budgetCost != null ? String(project.budgetCost) : '');
        setBudgetRevenue(project?.budgetRevenue != null ? String(project.budgetRevenue) : '');
        setAllowStaff(project?.allowStaffTagging ?? true);
        const inst = AuthService.getSelectedInstitution();
        if (inst) {
            CustomerService.listByInstitution(inst, 0, 200, 'ALL')
                .then(p => setCustomers((p.content ?? []).map(c => ({ id: c.id, name: c.name }))))
                .catch(() => setCustomers([])); // no CUSTOMER_VIEW: the field stays empty
        }
    }, [open, project]);

    const save = async () => {
        if (!code.trim() || !name.trim()) { toast.showError('Code and name are required'); return; }
        const body: ProjectRequest = {
            code: code.trim(), name: name.trim(), description: description.trim() || null,
            customerId: customerId || null, managerUserId: manager?.id ?? null,
            departmentId: departmentId || null,
            startDate: startDate || null, endDate: endDate || null,
            budgetCost: num(budgetCost), budgetRevenue: num(budgetRevenue), allowStaffTagging: allowStaff,
        };
        setSaving(true);
        try {
            const saved = project ? await ProjectService.update(project.id, body) : await ProjectService.create(body);
            resetProjectOptionsCache();
            toast.showSuccess(project ? 'Project updated' : 'Project created');
            onSaved(saved);
        } catch (err) {
            toast.showApiError(err, 'Could not save the project');
        } finally {
            setSaving(false);
        }
    };

    const cell = { flex: '1 1 200px', minWidth: 0 };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-help-page="projects.form.page">
            <DialogTitle>{project ? `Edit ${project.code}` : 'New project'}</DialogTitle>
            <DialogContent>
                <Box data-help-section="projects.form.section.details" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                    <TextField data-help="projects.form.field.code" label="Code" value={code} onChange={e => setCode(e.target.value)} required
                        inputProps={{ maxLength: 32, 'data-testid': 'project-code' }} sx={cell} />
                    <TextField data-help="projects.form.field.name" label="Name" value={name} onChange={e => setName(e.target.value)} required
                        inputProps={{ maxLength: 200, 'data-testid': 'project-name' }} sx={{ flex: '2 1 260px', minWidth: 0 }} />
                    <TextField data-help="projects.form.field.description" label="Description" value={description} onChange={e => setDescription(e.target.value)}
                        multiline minRows={2} fullWidth />
                    <Box sx={cell}>
                        <SearchableSelect<NamedOption> data-help="projects.form.field.customer" options={customers} value={customerId} onChange={setCustomerId}
                            getOptionLabel={c => c.name} label="Customer (optional)" placeholder="(none)" />
                    </Box>
                    {departmentOptions.length > 0 && (
                        <Box sx={cell}>
                            <DepartmentPicker data-help="projects.form.field.department" options={departmentOptions} value={departmentId} onChange={setDepartmentId}
                                label="Department (optional)" placeholder="(none)" />
                        </Box>
                    )}
                    <Box sx={cell}>
                        <EmployeeAutocomplete<UserOption> data-help="projects.form.field.manager" label="Manager (optional)" placeholder="Search users..." fullWidth
                            value={manager} onChange={setManager} onError={err => toast.showApiError(err, 'Failed to search users')} />
                    </Box>
                    <TextField data-help="projects.form.field.startDate" label="Start date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={cell} />
                    <TextField data-help="projects.form.field.endDate" label="End date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={cell} />
                    <TextField data-help="projects.form.field.budgetCost" label="Budget cost" type="number" value={budgetCost} onChange={e => setBudgetCost(e.target.value)}
                        inputProps={{ min: 0, step: '0.01' }} sx={cell} />
                    <TextField data-help="projects.form.field.budgetRevenue" label="Budget revenue" type="number" value={budgetRevenue} onChange={e => setBudgetRevenue(e.target.value)}
                        inputProps={{ min: 0, step: '0.01' }} sx={cell} />
                    <FormControlLabel data-help="projects.form.field.allowStaff" control={<Checkbox checked={allowStaff} onChange={e => setAllowStaff(e.target.checked)} />}
                        label="Staff can tag claims, vouchers and tasks to this project" />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="projects.form.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="projects.form.save" variant="contained" onClick={save} disabled={saving} data-testid="project-save">Save</Button>
            </DialogActions>
        </Dialog>
    );
};

interface StatusProps {
    project: Project | null;
    target: ProjectStatus | null;
    onClose: () => void;
    onDone: (p: Project) => void;
}

/** Status change confirm. Close takes a date (default today) and a note; reopen needs a reason (kept in the audit note). */
export const ProjectStatusDialog: React.FC<StatusProps> = ({ project, target, onClose, onDone }) => {
    const toast = useToast();
    const [closedOn, setClosedOn] = useState(toLocalIsoDate());
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    useEffect(() => { setClosedOn(toLocalIsoDate()); setNote(''); }, [project, target]);
    if (!project || !target) return null;
    const reopening = project.status === 'CLOSED';
    const closing = target === 'CLOSED';
    const submit = async () => {
        if (reopening && !note.trim()) { toast.showError('A reason is required to reopen a project'); return; }
        setSaving(true);
        try {
            const updated = await ProjectService.changeStatus(project.id, {
                status: target, closedOn: closing ? closedOn || null : null, note: note.trim() || null,
            });
            resetProjectOptionsCache();
            toast.showSuccess(`${project.code}: ${statusLabel[updated.status]}`);
            onDone(updated);
        } catch (err) {
            toast.showApiError(err, 'Could not change the status');
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth data-help-page="projects.statusDialog.page">
            <DialogTitle>{actionLabel(project.status, target)} {project.code}</DialogTitle>
            <DialogContent>
                <Box data-help-section="projects.statusDialog.section.change" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {closing && (
                        <>
                            <Typography variant="body2">
                                Postings dated after the close date will be refused until the project is reopened.
                                Reversals and year-end entries are not affected.
                            </Typography>
                            <TextField data-help="projects.statusDialog.field.closedOn" label="Closed on" type="date" value={closedOn} onChange={e => setClosedOn(e.target.value)}
                                InputLabelProps={{ shrink: true }} inputProps={{ max: toLocalIsoDate() }} fullWidth />
                        </>
                    )}
                    <TextField data-help="projects.statusDialog.field.note" label={reopening ? 'Reason for reopening' : 'Note (optional)'} value={note} required={reopening}
                        onChange={e => setNote(e.target.value)} multiline minRows={2} fullWidth
                        inputProps={{ maxLength: 500, 'data-testid': 'status-note' }} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="projects.statusDialog.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="projects.statusDialog.confirm" variant="contained" color={closing ? 'warning' : 'primary'} onClick={submit} disabled={saving}
                    data-testid="status-confirm">
                    {actionLabel(project.status, target)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
